/**
 * BaaS card issuer accounts (IssuerAccount) via MobileService `/v1/ib/console/*` → BaaS.
 *
 *   GET    /v1/cards                         scope cards.read
 *   GET    /v1/cards/{issuer_account_id}     scope cards.read
 *   POST   /v1/cards                                      scope cards.create
 *   POST   /v1/cards/{id}/link                            scope cards.write
 *   DELETE /v1/cards/{id}/link                            scope cards.write
 *   PATCH  /v1/cards/{id}/credit-limit                    scope cards.write
 *   POST   /v1/cards/{id}/physical-cards                  scope cards.write
 *   GET    /v1/cards/{id}/physical-cards                  scope cards.read
 *   GET    /v1/cards/{id}/physical-cards/{issuer_card_id} scope cards.read
 *
 * External ids: issacct_{guid:N}, isscard_{guid:N}.
 */

import { baasProxy } from "./baasConsoleClient";

const ISO4217_NUMERIC_TO_ALPHA = {
  986: "BRL",
  840: "USD",
  978: "EUR",
};

const alphaToNumeric = {
  BRL: 986,
  USD: 840,
  EUR: 978,
};

const formatMoneyMinor = (amount, currencyCode) => {
  if (amount == null || amount === "") return null;
  const numeric = Number(currencyCode) || 986;
  const alpha = ISO4217_NUMERIC_TO_ALPHA[numeric] || "BRL";
  // Amounts are stored as whole currency units (decimal(18,0)), not cents.
  return {
    amount: Number(amount) || 0,
    currencyCode: numeric,
    currency: alpha,
  };
};

/**
 * Map BaaS IssuerAccountResponse → console list/detail view model.
 * @param {object} api
 * @param {object} [instance]
 */
export const mapBaasCardAccountToUi = (api, instance = null) => {
  if (!api) return null;

  const creditLimit = formatMoneyMinor(
    api.credit_info?.credit_limit?.amount,
    api.credit_info?.credit_limit?.currency_code,
  );
  const withdrawalLimit = formatMoneyMinor(
    api.credit_info?.withdrawal_limit?.amount,
    api.credit_info?.withdrawal_limit?.currency_code,
  );

  return {
    id: api.id,
    issuerAccountId: api.issuer_account_id,
    status: String(api.status || "").trim().toUpperCase() || "PENDING",
    personId: api.person_id || null,
    personName: api.person_name || null,
    entityId: api.entity_id || null,
    accountId: api.account_id || null,
    cardProgramId: api.card_program_id || null,
    cardProgramName: api.card_program_name || null,
    psProductCode: api.ps_product_code || api.product?.code || null,
    product: api.product
      ? {
          code: api.product.code,
          name: api.product.name,
          kind: api.product.kind || null,
          supportsCreditInfo: Boolean(api.product.supports_credit_info),
        }
      : null,
    processorAccountId: api.processor_account_id || null,
    billingAddress: api.billing_address || null,
    cardDeliveryAddress: api.card_delivery_address || null,
    creditInfo: api.credit_info
      ? {
          creditLimit,
          withdrawalLimit,
          paymentDue: api.credit_info.payment_due ?? null,
        }
      : null,
    creditReserveHeld: Number(api.credit_reserve_held ?? 0) || 0,
    programReserve: api.program_reserve
      ? {
          guaranteePercent: Number(api.program_reserve.guarantee_percent ?? 0) || 0,
          balance: Number(api.program_reserve.balance ?? 0) || 0,
          held: Number(api.program_reserve.held ?? 0) || 0,
          available: Number(api.program_reserve.available ?? 0) || 0,
        }
      : null,
    registerDate: api.register_date || null,
    instanceId: instance?.id ?? null,
    raw: api,
  };
};

/**
 * @param {object} form
 * @returns {object} CreateIssuerAccountRequest (snake_case)
 */
export const buildCreateIssuerAccountRequest = (form = {}) => {
  const mapAddress = (address = {}) => ({
    address_line1: String(address.addressLine1 || "").trim(),
    address_line2: String(address.addressLine2 || "").trim() || null,
    neighborhood: String(address.neighborhood || "").trim() || null,
    city: String(address.city || "").trim(),
    state: String(address.state || "").trim(),
    zipcode: String(address.zipcode || "").trim(),
    country_code: String(address.countryCode || "BR").trim().toUpperCase() || "BR",
  });

  const body = {
    issuer_account_id: form.issuerAccountId || crypto.randomUUID(),
    card_program_id: String(form.cardProgramId || "").trim(),
    billing_address: mapAddress(form.billingAddress),
    card_delivery_address: mapAddress(form.cardDeliveryAddress),
  };

  const personId = String(form.personId || "").trim();
  if (personId) {
    body.person_id = personId;
  } else {
    body.account_owner = {
      full_name: String(form.fullName || "").trim(),
      identity_document_number: String(form.identityDocumentNumber || "").trim(),
      contact_information: {
        personal_phone_number1: String(form.phone || "").trim() || null,
        email: String(form.email || "").trim() || null,
      },
    };
  }

  const accountId = String(form.accountId || "").trim();
  if (accountId) body.account_id = accountId;

  if (form.includeCreditInfo) {
    const currencyCode =
      Number(form.creditCurrencyCode) ||
      alphaToNumeric[String(form.creditCurrency || "BRL").toUpperCase()] ||
      986;
    body.credit_info = {
      credit_limit: {
        amount: Number(form.creditLimitAmount) || 0,
        currency_code: currencyCode,
      },
      withdrawal_limit: {
        amount: Number(form.withdrawalLimitAmount) || 0,
        currency_code: currencyCode,
      },
      payment_due: Number(form.paymentDue) || null,
    };
  }

  return body;
};

/**
 * @param {object} api
 * @param {object} [instance]
 */
export const mapBaasPhysicalCardToUi = (api, instance = null) => {
  if (!api) return null;
  return {
    id: api.id,
    issuerCardId: api.issuer_card_id,
    issuerAccountId: api.issuer_account_id,
    accountId: api.account_id || null,
    status: String(api.status || "").trim().toUpperCase() || "PENDING",
    statusNote: api.status_note || null,
    psProductCode: api.ps_product_code || null,
    deliveryKitCode: api.delivery_kit_code || null,
    embossingName: api.embossing_name || null,
    mask: api.mask || null,
    brand: api.brand || null,
    processorCardId: api.processor_card_id || null,
    inhibitPhysicalCard: Boolean(api.inhibit_physical_card),
    issuerRequestId: api.issuer_request_id || null,
    cardDeliveryAddress: api.card_delivery_address || null,
    registerDate: api.register_date || null,
    instanceId: instance?.id ?? null,
    raw: api,
  };
};

/**
 * @param {object} form
 * @returns {object} CreatePhysicalCardRequest (snake_case)
 */
export const buildCreatePhysicalCardRequest = (form = {}) => {
  const body = {
    issuer_request_id: form.issuerRequestId || crypto.randomUUID(),
    issuer_card_id: form.issuerCardId || crypto.randomUUID(),
    delivery_kit_code: String(form.deliveryKitCode || "").trim() || null,
    inhibit_physical_card: Boolean(form.inhibitPhysicalCard),
    cardholder: {
      cardholder_type: String(form.cardholderType || "main").trim().toLowerCase() || "main",
      full_name: String(form.fullName || "").trim(),
      card_data: {
        embossing_name: String(form.embossingName || "").trim().toUpperCase(),
      },
      identity_document_number: String(form.identityDocumentNumber || "").trim(),
      birth_date: String(form.birthDate || "").trim(),
      nationality: String(form.nationality || "Brasil").trim() || "Brasil",
      gender: String(form.gender || "").trim() || null,
      civil_status: String(form.civilStatus || "").trim() || null,
      contact_information: {
        personal_phone_number1: String(form.phone || "").trim() || null,
        email: String(form.email || "").trim() || null,
      },
    },
  };

  if (form.psProductCode) body.ps_product_code = String(form.psProductCode).trim();

  if (form.cardDeliveryAddress) {
    const a = form.cardDeliveryAddress;
    body.card_delivery_address = {
      address_line1: String(a.address_line1 || a.addressLine1 || "").trim(),
      address_line2: String(a.address_line2 || a.addressLine2 || "").trim() || null,
      neighborhood: String(a.neighborhood || "").trim() || null,
      city: String(a.city || "").trim(),
      state: String(a.state || "").trim(),
      zipcode: String(a.zipcode || "").trim(),
      country_code: String(a.country_code || a.countryCode || "BR").trim().toUpperCase() || "BR",
    };
  }

  return body;
};

/**
 * @param {object} instance
 * @param {{ programId?: string, status?: string, personId?: string, signal?: AbortSignal }} [options]
 */
export const fetchBaasCardAccounts = async (instance, options = {}) => {
  if (!instance) return [];

  const query = {};
  if (options.programId) query.program_id = options.programId;
  if (options.status) query.status = options.status;
  if (options.personId) query.person_id = options.personId;

  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: "v1/cards",
    query: Object.keys(query).length ? query : undefined,
    signal: options.signal,
  });

  const list = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
  return list.map((item) => mapBaasCardAccountToUi(item, instance)).filter(Boolean);
};

export const fetchBaasCardAccountById = async (instance, issuerAccountId, options = {}) => {
  if (!instance || !issuerAccountId) return null;

  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: `v1/cards/${encodeURIComponent(issuerAccountId)}`,
    signal: options.signal,
  });

  return mapBaasCardAccountToUi(data, instance);
};

export const createBaasCardAccount = async (instance, form, options = {}) => {
  if (!instance) {
    const err = new Error("BaaS instance is required");
    err.name = "BaasConsoleError";
    err.code = "instance_required";
    throw err;
  }

  const body = buildCreateIssuerAccountRequest(form);
  const data = await baasProxy({
    instance,
    method: "POST",
    baasPath: "v1/cards",
    body,
    signal: options.signal,
  });

  return mapBaasCardAccountToUi(data, instance);
};

export const linkBaasCardAccount = async (instance, issuerAccountId, accountId, options = {}) => {
  if (!instance || !issuerAccountId || !accountId) {
    const err = new Error("instance, issuerAccountId and accountId are required");
    err.name = "BaasConsoleError";
    err.code = "validation_error";
    throw err;
  }

  const data = await baasProxy({
    instance,
    method: "POST",
    baasPath: `v1/cards/${encodeURIComponent(issuerAccountId)}/link`,
    body: { account_id: String(accountId).trim() },
    signal: options.signal,
  });

  return mapBaasCardAccountToUi(data, instance);
};

export const unlinkBaasCardAccount = async (instance, issuerAccountId, options = {}) => {
  if (!instance || !issuerAccountId) {
    const err = new Error("instance and issuerAccountId are required");
    err.name = "BaasConsoleError";
    err.code = "validation_error";
    throw err;
  }

  const data = await baasProxy({
    instance,
    method: "DELETE",
    baasPath: `v1/cards/${encodeURIComponent(issuerAccountId)}/link`,
    signal: options.signal,
  });

  return mapBaasCardAccountToUi(data, instance);
};

/**
 * @param {object} instance
 * @param {string} issuerAccountId
 * @param {{ creditLimitAmount: number|string, withdrawalLimitAmount?: number|string, paymentDue?: number|string, creditCurrencyCode?: number }} form
 */
export const patchBaasCardAccountCredit = async (
  instance,
  issuerAccountId,
  form,
  options = {},
) => {
  if (!instance || !issuerAccountId) {
    const err = new Error("instance and issuerAccountId are required");
    err.name = "BaasConsoleError";
    err.code = "validation_error";
    throw err;
  }

  const currencyCode = Number(form.creditCurrencyCode) || 986;
  const body = {
    credit_info: {
      credit_limit: {
        amount: Number(form.creditLimitAmount) || 0,
        currency_code: currencyCode,
      },
    },
  };

  if (form.withdrawalLimitAmount != null && form.withdrawalLimitAmount !== "") {
    body.credit_info.withdrawal_limit = {
      amount: Number(form.withdrawalLimitAmount) || 0,
      currency_code: currencyCode,
    };
  }

  if (form.paymentDue != null && form.paymentDue !== "") {
    body.credit_info.payment_due = Number(form.paymentDue) || null;
  }

  const data = await baasProxy({
    instance,
    method: "PATCH",
    baasPath: `v1/cards/${encodeURIComponent(issuerAccountId)}/credit-limit`,
    body,
    signal: options.signal,
  });

  return mapBaasCardAccountToUi(data, instance);
};

export const fetchBaasPhysicalCards = async (instance, issuerAccountId, options = {}) => {
  if (!instance || !issuerAccountId) return [];

  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: `v1/cards/${encodeURIComponent(issuerAccountId)}/physical-cards`,
    signal: options.signal,
  });

  const list = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
  return list.map((item) => mapBaasPhysicalCardToUi(item, instance)).filter(Boolean);
};

export const createBaasPhysicalCard = async (instance, issuerAccountId, form, options = {}) => {
  if (!instance || !issuerAccountId) {
    const err = new Error("instance and issuerAccountId are required");
    err.name = "BaasConsoleError";
    err.code = "validation_error";
    throw err;
  }

  const body = buildCreatePhysicalCardRequest(form);
  const data = await baasProxy({
    instance,
    method: "POST",
    baasPath: `v1/cards/${encodeURIComponent(issuerAccountId)}/physical-cards`,
    body,
    signal: options.signal,
  });

  return mapBaasPhysicalCardToUi(data, instance);
};
